import { standardGraphColors } from "../theme/siteColors";
import { getDesiredStartingColorModIndex } from "./colorIndexHelper";

import _ from "lodash";
import type { IntegrationDTO } from "@lib/ShiOneClient";
import { IntegrationType } from "@lib/ShiOneClient";

const months = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

interface IntegrationDefinition {
  integrationType: string;
}

export function getIntegrationDefinitionFromTypeId(
  typeId: number,
  integrationsDefinitions: IntegrationDefinition[],
): IntegrationDefinition | null | undefined {
  const matchingIntegrationType = Object.keys(IntegrationType).find(
    (key) => (IntegrationType[key as keyof typeof IntegrationType] as unknown as number) === typeId,
  );

  if (!matchingIntegrationType) {
    return null;
  }

  return integrationsDefinitions.find((integrationDefinition) => {
    return (
      integrationDefinition.integrationType.toLowerCase() ===
      matchingIntegrationType.toLowerCase()
    );
  });
}

export function getTypeIdFromIntegrationDefinition(integrationDefinition: IntegrationDefinition | null | undefined) {
  const key = Object.keys(IntegrationType).find((key) => {
    return (
      key &&
      integrationDefinition &&
      integrationDefinition.integrationType &&
      key.toLowerCase() === integrationDefinition.integrationType.toLowerCase()
    );
  });

  if (IntegrationType[key as keyof typeof IntegrationType]) {
    return IntegrationType[key as keyof typeof IntegrationType];
  }

  return null;
}

interface IntegrationsCollection {
  integrationsCollectorFetchStatus: string;
  integrations: Array<
    Pick<IntegrationDTO, "integrationType" | "activated">
  >;
}

export function hasIntegrationsByType(integrationsCollection: IntegrationsCollection, integrationType: string): boolean {
  const { integrationsCollectorFetchStatus, integrations } =
    integrationsCollection;
  return (
    integrationsCollectorFetchStatus === "complete" &&
    integrations &&
    integrations.length > 0 &&
    _.some(
      integrations,
      (integration) =>
        integration.integrationType === integrationType &&
        integration.activated === true,
    )
  );
}

interface CostAnalyticsEntry {
  month: number;
  year: number;
  monthlyCost: number;
  numberOfAccounts: number;
  projectedCost: number;
  dailyAverageCost: number;
}

interface CostDataResponse {
  response: Record<string, { accounts: Record<string, CostAnalyticsEntry[]> }>;
}

// Module-scoped accumulator for Chart.js tooltip total
let tooltipTotal = 0;

export interface CloudReportAccess {
  aws: boolean;
  azure: boolean;
  gcp: boolean;
}

export function getCloudReportInformation(costData: CostDataResponse, isAdmin: boolean, reportAccess: CloudReportAccess) {
  const awsReport = "aws_cost/history";
  const azureReport = "azure_cost/history";
  const gcpReport = "gcp_cost/history";

  const permissions = {
    AWS: isAdmin || reportAccess.aws,
    Azure: isAdmin || reportAccess.azure,
    AzureDirect: isAdmin || reportAccess.azure,
    GCP: isAdmin || reportAccess.gcp,
  };

  let hasAwsReport = false;
  let hasAzureReport = false;
  let hasAzureDirectReport = false;
  let hasGcpReport = false;
  let fetchCloudReportsComplete = false;

  let awsAccounts = 0;
  let azureSubscriptions = 0;
  let azureDirectSubscriptions = 0;
  let gcpAccounts = 0;

  const skeletonCloudReportChartData = {
    labels: [] as string[],
    datasets: [] as Record<string, unknown>[],
  };

  let cloudCurrentCost = 0;
  let awsLastMonthCost = 0;
  let azureLastMonthCost = 0;
  let azureDirectLastMonthCost = 0;
  let gcpLastMonthCost = 0;
  let cloudLastMonthCost = 0;
  let projectedCost = 0;
  let dailyAverageCost = 0;

  const cloudChartJsData = {
    datasets: [] as Record<string, unknown>[],
    labels: [] as string[],
  };

  let colorIndex = 0;

  _.forEach(costData.response, (value, key) => {
    const monthlyCosts = [0, 0, 0];
    let hasNonZeroCost = false;

    if (Object.keys(value.accounts).length === 0 || !permissions[key as keyof typeof permissions]) {
      return;
    }

    for (let acctKey of Object.keys(value.accounts)) {
      let costAnalytics = value.accounts[acctKey];
      costAnalytics = _.orderBy(costAnalytics, ["month"], ["asc"]);

      const currentMonth = new Date().getMonth() + 1;

      const currentMonthDate = new Date();
      currentMonthDate.setDate(1);

      const lastMonth = new Date();
      lastMonth.setDate(1);
      lastMonth.setMonth(lastMonth.getMonth() - 1);

      const twoMonthsPrior = new Date();
      twoMonthsPrior.setDate(1);
      twoMonthsPrior.setMonth(twoMonthsPrior.getMonth() - 2);

      _.forEach(costAnalytics, (a) => {
        const costDate = new Date(a.year, a.month);
        if (costDate >= twoMonthsPrior) {
          if (costDate >= currentMonthDate) {
            monthlyCosts[2] += a.monthlyCost;
            hasNonZeroCost = true;
          } else if (costDate >= lastMonth) {
            monthlyCosts[1] += a.monthlyCost;
            hasNonZeroCost = true;
            //only want to count accounts for last month
            if (key === "AWS") {
              awsAccounts += a.numberOfAccounts;
            } else if (key === "Azure") {
              azureSubscriptions += a.numberOfAccounts;
            } else if (key === "GCP") {
              gcpAccounts += a.numberOfAccounts;
            } else if (key === "AzureDirect") {
              azureDirectSubscriptions += a.numberOfAccounts;
            }
          } else {
            monthlyCosts[0] += a.monthlyCost;
            hasNonZeroCost = true;
          }

          if (_.indexOf(cloudChartJsData.labels, months[a.month - 1]) === -1) {
            cloudChartJsData.labels.push(months[a.month - 1]);
          }

          if (a.month === currentMonth) {
            projectedCost += a.projectedCost;
            cloudCurrentCost += a.monthlyCost;
            dailyAverageCost += a.dailyAverageCost;
          }
          if (key === "AWS") {
            hasAwsReport = true;
          } else if (key === "Azure") {
            hasAzureReport = true;
          } else if (key === "GCP") {
            hasGcpReport = true;
          } else if (key === "AzureDirect") {
            hasAzureDirectReport = true;
          }

          if (
            a.month === lastMonth.getMonth() + 1 &&
            a.year === lastMonth.getFullYear()
          ) {
            if (key === "AWS") {
              awsLastMonthCost += a.monthlyCost;
            } else if (key === "Azure") {
              azureLastMonthCost += a.monthlyCost;
            } else if (key === "GCP") {
              gcpLastMonthCost += a.monthlyCost;
            } else if (key === "AzureDirect") {
              azureDirectLastMonthCost += a.monthlyCost;
            }

            cloudLastMonthCost += a.monthlyCost;
          }

          fetchCloudReportsComplete = true;
        }
      });
    }
    const cloudCostHistoryStartingColorIndex = 13;
    const cloudCostHistoryColorIndex = getDesiredStartingColorModIndex(
      colorIndex,
      standardGraphColors.length,
      cloudCostHistoryStartingColorIndex,
    );

    if (hasNonZeroCost) {
      cloudChartJsData.datasets.push({
        backgroundColor: standardGraphColors[cloudCostHistoryColorIndex],
        hoverBackgroundColor: standardGraphColors[cloudCostHistoryColorIndex],
        data: monthlyCosts,
        label: key === "AzureDirect" ? "Azure Direct" : key,
      });
    }

    colorIndex++;
  });

  const totalAccounts =
    awsAccounts + azureSubscriptions + azureDirectSubscriptions + gcpAccounts;

  const cloudThreeMonthReportChartOptions = {
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
        ticks: {
          callback: function (value: number | string) {
            return (
              "$" +
              parseFloat(String(value)).toLocaleString(undefined, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })
            );
          },
        },
      },
    },
    tooltips: {
      enabled: true,
      mode: "label",
      callbacks: {
        afterTitle: function () {
          tooltipTotal = 0;
        },
        label: function (tooltipItems: Record<string, unknown>, data: Record<string, unknown>) {
          const datasets = data.datasets as Array<{ label: string }>;
          const cloud = datasets[tooltipItems.datasetIndex as number].label;
          tooltipTotal = tooltipTotal + (tooltipItems.yLabel as number);
          return cloud === "AzureDirect"
            ? "Azure Direct"
            : cloud +
                ": $" +
                parseFloat(String(tooltipItems.yLabel)).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                });
        },
        footer: function () {
          return (
            "Total : $" +
            parseFloat(String(tooltipTotal)).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })
          );
        },
      },
    },
  };

  return {
    awsReport,
    azureReport,
    gcpReport,
    hasAwsReport,
    hasAzureReport,
    hasAzureDirectReport,
    hasGcpReport,
    fetchCloudReportsComplete,
    cloudChartJsData,
    skeletonCloudReportChartData,
    cloudThreeMonthReportChartOptions,
    cloudCurrentCost,
    dailyAverageCost,
    awsLastMonthCost,
    azureLastMonthCost,
    azureDirectLastMonthCost,
    gcpLastMonthCost,
    cloudLastMonthCost,
    projectedCost,
    totalAccounts,
    awsAccounts,
    azureSubscriptions,
    azureDirectSubscriptions,
    gcpAccounts,
  };
}
